import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "heroCarousel", "heroImage",
    "lightbox", "lightboxCarousel", "lightboxImage", "lightboxCounter",
    "zoomLabel", "zoomInBtn", "zoomOutBtn",
    "title", "counter", "loader", "thumbnail", "carousel"
  ]
  static values = { galleryId: String }

  declare readonly heroCarouselTarget: HTMLElement
  declare readonly heroImageTargets: HTMLElement[]
  declare readonly lightboxTarget: HTMLElement
  declare readonly lightboxCarouselTarget: HTMLElement
  declare readonly lightboxImageTargets: HTMLElement[]
  declare readonly lightboxCounterTarget: HTMLElement
  declare readonly hasLightboxCounterTarget: boolean
  declare readonly zoomLabelTarget: HTMLElement
  declare readonly hasZoomLabelTarget: boolean
  declare readonly zoomInBtnTarget: HTMLButtonElement
  declare readonly hasZoomInBtnTarget: boolean
  declare readonly zoomOutBtnTarget: HTMLButtonElement
  declare readonly hasZoomOutBtnTarget: boolean
  declare readonly titleTarget: HTMLElement
  declare readonly hasTitleTarget: boolean
  declare readonly counterTarget: HTMLElement
  declare readonly hasCounterTarget: boolean
  declare readonly thumbnailTargets: HTMLElement[]
  declare readonly carouselTarget: HTMLElement

  private images: Array<{ title: string; index: number }> = []
  private currentIndex = 0
  private isSyncing = false
  private zoomLevel = 1
  private zoomDirection = 1
  private isPanning = false
  private startX = 0
  private startY = 0
  private translateX = 0
  private translateY = 0

  connect() {
    this.collectImages()
    this.updateUI()
  }

  collectImages() {
    this.images = this.thumbnailTargets.map((item, index) => ({
      title: (item as HTMLElement).dataset.galleryTitle || "",
      index
    }))
  }

  // ── Main gallery ────────────────────────────────────────────────────────────

  select(event: MouseEvent) {
    event.preventDefault()
    this.currentIndex = parseInt((event.currentTarget as HTMLElement).dataset.galleryIndex || "0")
    this.syncAllViews(false)
  }

  next() { this.currentIndex = (this.currentIndex + 1) % this.images.length; this.resetZoom(); this.syncAllViews() }
  prev() { this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length; this.resetZoom(); this.syncAllViews() }

  onHeroScroll() {
    if (this.isSyncing) return
    const newIndex = this.calculateIndexFromScroll(this.heroCarouselTarget)
    if (newIndex !== this.currentIndex) { this.currentIndex = newIndex; this.updateUI() }
  }

  // ── Lightbox ────────────────────────────────────────────────────────────────

  openLightbox(event: MouseEvent) {
    if (event) event.preventDefault()
    this.lightboxTarget.classList.add("is-open")
    this.syncLightboxView(false)
    this.updateZoomUI()
    document.body.style.overflow = "hidden"
  }

  closeLightbox() {
    this.resetZoom()
    this.lightboxTarget.classList.remove("is-open")
    document.body.style.overflow = ""
  }

  nextLightbox() { this.resetZoom(); this.currentIndex = (this.currentIndex + 1) % this.images.length; this.syncAllViews() }
  prevLightbox() { this.resetZoom(); this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length; this.syncAllViews() }

  onLightboxScroll() {
    if (this.isSyncing || this.zoomLevel > 1) return
    const newIndex = this.calculateIndexFromScroll(this.lightboxCarouselTarget)
    if (newIndex !== this.currentIndex) { this.currentIndex = newIndex; this.updateUI() }
  }

  handleKeydown(event: KeyboardEvent) {
    if (!this.lightboxTarget.classList.contains("is-open")) return
    if (event.key === "Escape")      this.closeLightbox()
    if (event.key === "ArrowRight")  this.nextLightbox()
    if (event.key === "ArrowLeft")   this.prevLightbox()
    if (event.key === "+")           this.zoomIn()
    if (event.key === "-")           this.zoomOut()
  }

  // ── Zoom ────────────────────────────────────────────────────────────────────

  handleImageClick(event: MouseEvent) {
    if (event) event.stopPropagation()
    if (this.zoomLevel === 1)       { this.zoomDirection = 1;  this.zoomIn() }
    else if (this.zoomLevel === 4)  { this.zoomDirection = -1; this.zoomOut() }
    else                            { this.zoomDirection === 1 ? this.zoomIn() : this.zoomOut() }
  }

  zoomIn(event?: MouseEvent) {
    if (event) event.stopPropagation()
    const img = this.lightboxImageTargets[this.currentIndex]?.querySelector("img")
    if (!img) return
    if (this.zoomLevel === 1) this.zoomLevel = 2
    else if (this.zoomLevel === 2) this.zoomLevel = 4
    this.applyZoom(img)
  }

  zoomOut(event?: MouseEvent) {
    if (event) event.stopPropagation()
    const img = this.lightboxImageTargets[this.currentIndex]?.querySelector("img")
    if (!img) return
    if (this.zoomLevel === 4) this.zoomLevel = 2
    else if (this.zoomLevel === 2) this.zoomLevel = 1
    this.applyZoom(img)
  }

  resetZoom(event?: MouseEvent) {
    if (event) event.stopPropagation()
    if (this.zoomLevel === 1) return
    this.zoomLevel = 1; this.zoomDirection = 1; this.translateX = 0; this.translateY = 0
    const img = this.lightboxImageTargets[this.currentIndex]?.querySelector("img")
    if (img) this.applyZoom(img)
    this.updateZoomUI()
  }

  private applyZoom(img: HTMLImageElement) {
    if (this.zoomLevel > 1) {
      img.style.transform = `scale(${this.zoomLevel}) translate(${this.translateX / this.zoomLevel}px, ${this.translateY / this.zoomLevel}px)`
      this.lightboxCarouselTarget.style.overflowX = "hidden"
    } else {
      img.style.transform = ""
      this.lightboxCarouselTarget.style.overflowX = "auto"
    }
    this.updateZoomUI()
  }

  // ── Pan ─────────────────────────────────────────────────────────────────────

  startPan(event: MouseEvent | TouchEvent) {
    if (this.zoomLevel === 1) return
    this.isPanning = true
    const p = this.getPoint(event)
    this.startX = p.x - this.translateX; this.startY = p.y - this.translateY
    ;(event.currentTarget as HTMLElement).style.cursor = "grabbing"
  }

  pan(event: MouseEvent | TouchEvent) {
    if (!this.isPanning || this.zoomLevel === 1) return
    event.preventDefault()
    const p = this.getPoint(event)
    const img = event.currentTarget as HTMLImageElement
    const bounds = this.calculateBounds(img)
    this.translateX = Math.max(Math.min(p.x - this.startX, bounds.maxX), bounds.minX)
    this.translateY = Math.max(Math.min(p.y - this.startY, bounds.maxY), bounds.minY)
    this.applyZoom(img)
  }

  stopPan(event: MouseEvent | TouchEvent) {
    this.isPanning = false
    ;(event.currentTarget as HTMLElement).style.cursor = ""
  }

  private getPoint(event: MouseEvent | TouchEvent) {
    if (window.TouchEvent && event instanceof TouchEvent) return { x: event.touches[0].clientX, y: event.touches[0].clientY }
    return { x: (event as MouseEvent).clientX, y: (event as MouseEvent).clientY }
  }

  private calculateBounds(img: HTMLImageElement) {
    const rect = img.getBoundingClientRect()
    const parent = img.parentElement?.getBoundingClientRect()
    if (!parent) return { minX: 0, maxX: 0, minY: 0, maxY: 0 }
    return {
      minX: -(rect.width  - parent.width)  / 2, maxX: (rect.width  - parent.width)  / 2,
      minY: -(rect.height - parent.height) / 2, maxY: (rect.height - parent.height) / 2
    }
  }

  // ── UI sync ─────────────────────────────────────────────────────────────────

  private updateZoomUI() {
    if (this.hasZoomLabelTarget) this.zoomLabelTarget.textContent = `${this.zoomLevel}x`
    if (this.hasZoomInBtnTarget)  this.zoomInBtnTarget.disabled  = this.zoomLevel === 4
    if (this.hasZoomOutBtnTarget) this.zoomOutBtnTarget.disabled = this.zoomLevel === 1

    this.lightboxImageTargets.forEach(target => {
      const img = target.querySelector("img")
      if (!img) return
      const shouldZoomOut = this.zoomLevel === 4 || (this.zoomLevel === 2 && this.zoomDirection === -1)
      img.style.cursor = shouldZoomOut ? "zoom-out" : "zoom-in"
    })
  }

  private syncAllViews(smooth = true) {
    this.isSyncing = true
    this.syncHeroView(smooth)
    this.syncLightboxView(smooth)
    this.updateUI()
    setTimeout(() => { this.isSyncing = false }, 500)
  }

  private syncHeroView(smooth = true) {
    const target = this.heroImageTargets[this.currentIndex]
    if (target) target.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "nearest", inline: "center" })
  }

  private syncLightboxView(smooth = true) {
    if (!this.lightboxTarget.classList.contains("is-open")) return
    const target = this.lightboxImageTargets[this.currentIndex]
    if (target) target.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "nearest", inline: "center" })
  }

  updateUI() {
    const image = this.images[this.currentIndex]
    if (!image) return
    if (this.hasTitleTarget) this.titleTarget.textContent = image.title
    if (this.hasCounterTarget) this.counterTarget.textContent = `${this.currentIndex + 1} / ${this.images.length}`
    if (this.hasLightboxCounterTarget) this.lightboxCounterTarget.textContent = `${this.currentIndex + 1} / ${this.images.length}`

    this.thumbnailTargets.forEach((thumb, idx) => {
      thumb.classList.toggle("active", idx === this.currentIndex)
      if (idx === this.currentIndex) this.scrollToThumbnail(thumb)
    })
  }

  private scrollToThumbnail(thumb: HTMLElement) {
    const targetX = thumb.offsetLeft - this.carouselTarget.clientWidth / 2 + thumb.clientWidth / 2
    this.carouselTarget.scrollTo({ left: targetX, behavior: "smooth" })
  }

  private calculateIndexFromScroll(container: HTMLElement) {
    return Math.round(container.scrollLeft / container.clientWidth) || 0
  }

  scrollThumbNext() { this.carouselTarget.scrollBy({ left:  this.carouselTarget.clientWidth * 0.8, behavior: "smooth" }) }
  scrollThumbPrev() { this.carouselTarget.scrollBy({ left: -this.carouselTarget.clientWidth * 0.8, behavior: "smooth" }) }
}
